import { Guid } from '../../Common/models/Guid';
import { Deployment } from '../models/Deployment';
import {
    getTimelineProgress,
    getTimelineStepText,
    isFinalStepStatus,
    TimelineItem,
} from '../models/Timeline';
import { ReleaseSearchResult } from '../models/Release';
import { ReplicationStatusResult } from '../models/ReplicationStatusResult';
import { Replication } from '../models/Replication';
import { CloudEnvironment } from '../../Common/models/CloudEnvironment';
import { Timestamp } from '../../Common/models/Timestamp';
import { dateLocaleStringWithTimeZone } from '../../Common/util/DateUtils';

export const sortReleaseListDescending = (
    releases: ReleaseSearchResult[]
): ReleaseSearchResult[] => {
    return releases.sort((s1, s2) => {
        return new Date(s1.updatedOn || '') > new Date(s2.updatedOn || '')
            ? -1
            : 1;
    });
};

export const getReleaseDetailsPath = (releaseCorrelationId: string): string => {
    return `/ReleaseStatus/Release/${releaseCorrelationId}`;
};

const publicStatusTypes = new Set([10, 25, 30]);

export const getReplicationStatusCloud = (
    statusIndex: number,
    destinationEnvironment: string | undefined,
    statusEnvironment: string | undefined
): string => {
    return publicStatusTypes.has(statusIndex)
        ? CloudEnvironment.Public
        : destinationEnvironment || statusEnvironment || '';
};

export const getCurrentReplicationTimeline = (
    item: Replication | undefined
): TimelineItem[] => {
    if (item) {
        let statusList = item.statusList;
        let sortedList = sortStatusAscending(statusList);

        return sortedList.map((replicationStatusResult) => {
            let timestamp = replicationStatusResult?.isProjected
                ? replicationStatusResult?.projectedDate
                : replicationStatusResult?.updatedOn;
            return {
                name: replicationStatusResult?.name,
                description: replicationStatusResult?.description || '',
                pendingName: replicationStatusResult?.pendingName,
                pendingDescription: replicationStatusResult?.pendingDescription,
                cloud: getReplicationStatusCloud(
                    replicationStatusResult.index,
                    item.environmentId,
                    replicationStatusResult?.environmentId
                ),
                status: replicationStatusResult?.completionIndicator
                    ? replicationStatusResult?.completionIndicator
                    : getStepStatus(item.lastUpdated, timestamp),
                statusDetails: replicationStatusResult.statusDetails,
                timestamp: dateLocaleStringWithTimeZone(timestamp),
                isProjected: replicationStatusResult?.isProjected,
                isValid: replicationStatusResult?.isValid,
                shouldDisplay: replicationStatusResult?.shouldDisplay,
            };
        });
    }
    return [];
};

export const getCurrentDeploymentTimeline = (
    item: Deployment
): TimelineItem[] => {
    return getCurrentReplicationTimeline(item.currentReplication);
};

/**
 * The step the flyout timeline is marking, so a list row and the flyout cannot disagree. The
 * latest reached status is often already complete, in which case the running step is the next one.
 */
export const getCurrentStepName = (item: Replication | undefined): string => {
    const visibleTimeline = getCurrentReplicationTimeline(item).filter(
        (step) => step.shouldDisplay
    );

    if (visibleTimeline.length === 0) {
        return '';
    }

    const { inProgressIndex, endedAtIndex } =
        getTimelineProgress(visibleTimeline);
    const index =
        endedAtIndex !== -1
            ? endedAtIndex
            : inProgressIndex !== -1
            ? inProgressIndex
            : visibleTimeline.length - 1;
    const step = visibleTimeline[index];

    return getTimelineStepText(
        step.name,
        step.description,
        index === inProgressIndex,
        isFinalStepStatus(step.status),
        step.pendingName,
        step.pendingDescription
    ).name;
};

//check if time happened or is estimated future date
export const getStepStatus = (
    replicationTimestamp: Timestamp,
    statusTimestamp: Timestamp | undefined
): string => {
    if (statusTimestamp) {
        if (
            new Date(replicationTimestamp).getTime() >
            new Date(statusTimestamp).getTime()
        ) {
            return 'Completed';
        }
        if (
            new Date(replicationTimestamp).getTime() ===
            new Date(statusTimestamp).getTime()
        ) {
            return 'Processing';
        }
    }
    return 'NotStarted';
};

export const sortReplicationsDescending = (
    replications: Replication[]
): Replication[] => {
    return replications.sort((s1, s2) => {
        return new Date(s1.lastUpdated) > new Date(s2.lastUpdated) ? -1 : 1;
    });
};

export const sortStatusAscending = (
    statusList: ReplicationStatusResult[]
): ReplicationStatusResult[] => {
    return statusList.sort((s1, s2) => {
        return s1.index > s2.index ? 1 : -1;
    });
};
